/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Breadcrumb, IBreadcrumbItem } from '@fluentui/react/lib/Breadcrumb';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2, important } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';

type CluBreadcrumbProps = { serviceType: CluProjectType };

const messages = defineMessages({
    train: { id: 'clu.cluBreadcrumb.train', defaultMessage: '{ projectName } - Training jobs' },
    test: { id: 'clu.cluBreadcrumb.test', defaultMessage: '{ projectName } - Testing deployments' },
    deploy: { id: 'clu.cluBreadcrumb.deploy', defaultMessage: '{ projectName } - Deploying a model' },
    languageStudio: { id: 'clu.cluBreadcrumb.languageStudio', defaultMessage: 'Language Studio' },
    moreLinksAriaLabel: { id: 'clu.cluBreadcrumb.moreLinksAriaLabel', defaultMessage: 'More Links' },
    buildSchema: { id: 'clu.cluBreadcrumb.buildSchema', defaultMessage: '{ projectName } - Schema definition' },
    breadcrumbsAriaLabel: { id: 'clu.cluBreadcrumb.breadcrumbsAriaLabel', defaultMessage: 'Main Breadcrumb' },
    tagUtterances: { id: 'clu.cluBreadcrumb.tagUtterances', defaultMessage: '{ projectName } - Data labeling' },
    modelDetails: { id: 'clu.cluBreadcrumb.modelDetails', defaultMessage: '{ projectName } - Model performance' },
    orchestrationProjects: { id: 'clu.cluBreadcrumb.orchestrationProjects', defaultMessage: 'Orchestration Projects' },
    projectSettings: { id: 'clu.cluBreadcrumb.projectSettings', defaultMessage: '{ projectName } - Project settings' },
    entityComponents: { id: 'clu.cluBreadcrumb.entityComponents', defaultMessage: '{ entityName } - Entity components' },
    reviewEndpoint: { id: 'clu.cluBreadcrumb.reviewEndpoint', defaultMessage: '{ projectName } - Review endpoint utterances' },
    reviewPredictionSet: { id: 'clu.cluBreadcrumb.reviewPredictionSet', defaultMessage: '{ projectName } - Review predictions' },
    conversationProjects: {
        id: 'clu.cluBreadcrumb.conversationProjects',
        defaultMessage: 'Conversational Language Understanding projects'
    }
});

const { getClassName } = getStylistV2('CustomTextBreadcrumb');

const itemClassName = getClassName('item', important({ fontSize: '14px', fontWeight: 'normal' }));
const linkItemClassName = getClassName('linkItem', theme =>
    important({ color: theme.linkColor, fontSize: '14px', display: 'inline-block', height: '100%' })
);
const linkIconClassName = getClassName('linkIcon', important({ fontSize: '10px' }));

const useConversationBreadcrumbItems = (serviceType: CluProjectType) => {
    const history = useHistory();
    const { pathname } = useLocation();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const navigateTo = (path: string, state?: object) => () => history.push(path, state);

    const doesMatch = (path: string, isExact: boolean = true) =>
        doesRouteMatchLocation(path, { exact: isExact, locationToMatch: pathname });

    const items: IBreadcrumbItem[] = [
        { text: localize(messages.languageStudio), key: 'languageStudio', onClick: navigateTo(routeTable.languageStudio.root()) }
    ];

    const projectName = rootStore.currentCluProjectStore?.name;
    const routes = routeTable[serviceType === 'Conversation' ? 'clu' : 'orchestration'];

    const getModelNameFromPathName = () => pathname.split(routes.modelDetailsListPage(projectName))[1]?.split('/')[1] ?? '';

    if (doesMatch(routes.projectsPage())) {
        items.push({
            key: 'projects',
            text: localize(serviceType === 'Conversation' ? messages.conversationProjects : messages.orchestrationProjects)
        });
    } else if (doesMatch(routes.projectsPage(), false)) {
        items.push({
            key: 'projects',
            onClick: navigateTo(routes.projectsPage()),
            text: localize(serviceType === 'Conversation' ? messages.conversationProjects : messages.orchestrationProjects)
        });

        if (doesMatch(routes.buildSchemaPage())) {
            items.push({ key: 'buildSchema', text: localize(messages.buildSchema, { projectName }) });
        } else if (doesMatch(routes.buildSchemaPage(), false)) {
            items.push({
                key: 'buildSchema',
                text: localize(messages.buildSchema, { projectName }),
                onClick: navigateTo(routes.buildSchemaPage())
            });
        }

        if (doesMatch(routes.entityDetailsPage())) {
            const pathArr = window.location.pathname.split('/');
            const entityName = decodeURI(pathArr[pathArr.length - 1]);

            items.push({ key: 'entityComponents', text: localize(messages.entityComponents, { entityName }) });
        }
        if (doesMatch(routes.dataTaggingPage())) {
            items.push({ key: 'tagData', text: localize(messages.tagUtterances, { projectName }) });
        } else if (doesMatch(routes.trainingPage())) {
            items.push({ key: 'train', text: localize(messages.train, { projectName }) });
        } else if (doesMatch(routes.modelDetailsPage(projectName), false)) {
            items.push({
                key: 'viewModelDetails',
                onClick: navigateTo(routes.modelDetailsListPage()),
                text: localize(messages.modelDetails, { projectName })
            });
            if (getModelNameFromPathName()) {
                items.push({ text: getModelNameFromPathName(), key: 'viewModel' });
            }
        } else if (doesMatch(routes.modelDetailsListPage(), false)) {
            items.push({ key: 'viewModelDetails', text: localize(messages.modelDetails, { projectName }) });
        } else if (doesMatch(routes.reviewPredictionsPage())) {
            items.push({ key: 'reviewPredictions', text: localize(messages.reviewPredictionSet, { projectName }) });
        } else if (doesMatch(routes.deployModelPage())) {
            items.push({ key: 'deployModel', text: localize(messages.deploy, { projectName }) });
        } else if (doesMatch(routes.testModelsPage())) {
            items.push({ key: 'testModel', text: localize(messages.test, { projectName }) });
        } else if (doesMatch(routes.projectSettingsPage())) {
            items.push({ key: 'projectSettings', text: localize(messages.projectSettings, { projectName }) });
        }
    }

    return items;
};

export const CluBreadcrumb = observer((props: CluBreadcrumbProps) => {
    const { serviceType } = props;
    const { localize } = useLocalization();
    const items = useConversationBreadcrumbItems(serviceType);

    return (
        <Breadcrumb
            items={items}
            ariaLabel={localize(messages.breadcrumbsAriaLabel)}
            overflowAriaLabel={localize(messages.moreLinksAriaLabel)}
            styles={{
                root: { margin: 0 },
                item: itemClassName,
                chevron: linkIconClassName,
                itemLink: `${itemClassName} ${linkItemClassName}`
            }}
        />
    );
});
